import React, { createContext, useContext, useMemo, useState } from 'react';
import AdminService from '../../services/adminService';

const InlineEditContext = createContext(null);

export const useInlineEdit = () => useContext(InlineEditContext);

/**
 * Provider to enable inline editing within page components rendered inside Admin.
 * Props:
 * - slug: page slug (e.g., 'accueil', 'services')
 * - token: admin auth token
 * - editing: boolean indicating edit mode
 * - initialSections: optional array of sections to prime local cache
 */
export const InlineEditProvider = ({ slug, token, editing, initialSections = [], children }) => {
  const [sections, setSections] = useState(Array.isArray(initialSections) ? initialSections : []);

  const findSection = (key) => {
    if (!key) return null;
    return sections.find(s => s.key === key || s.type === key) || null;
  };

  const setSectionLocal = (nextSec) => {
    setSections(prev => {
      const idx = prev.findIndex(s => (s.key === nextSec.key) || (s.type === nextSec.type));
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = nextSec;
        return copy;
      }
      return [...prev, nextSec];
    });
  };

  const buildNestedObject = (path, value, base = {}) => {
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
    let cursor = base;
    for (let i = 0; i < parts.length; i++) {
      const p = parts[i];
      if (i === parts.length - 1) {
        cursor[p] = value;
      } else {
        if (typeof cursor[p] !== 'object' || cursor[p] == null) {
          // next is index?
          const nextIsIndex = !isNaN(Number(parts[i+1]));
          cursor[p] = nextIsIndex ? [] : {};
        }
        cursor = cursor[p];
      }
    }
    return base;
  };

  const readNested = (obj, path) => {
    if (!obj || !path) return undefined;
    const parts = path.replace(/\[(\d+)\]/g, '.$1').split('.').filter(Boolean);
    let cur = obj;
    for (const p of parts) {
      if (cur == null) return undefined;
      cur = cur[p];
    }
    return cur;
  };

  const updateField = async ({ sectionKey, fieldPath, value }) => {
    const existing = findSection(sectionKey);
    const base = existing ? { ...(existing.data || {}) } : {};
    const nextData = buildNestedObject(fieldPath, value, base);
    const nextSec = existing
      ? { ...existing, data: nextData }
      : { key: sectionKey, type: sectionKey, title: '', order: sections.length, actif: true, data: nextData };
    try {
      const updated = await AdminService.upsertPageSection(slug, nextSec, token);
      const sec = Array.isArray(updated?.sections)
        ? (updated.sections.find(s => (s.key === sectionKey || s.type === sectionKey)) || nextSec)
        : nextSec;
      setSectionLocal(sec);
      return sec;
    } catch (err) {
      const msg = String(err?.message || '');
      // Si la page n'existe pas encore, créer via upsertPage (fallback)
      if (/Page non trouvée|404/i.test(msg)) {
        const created = await AdminService.upsertPage({ slug, sections: [nextSec] }, token);
        const sec = Array.isArray(created?.sections)
          ? (created.sections.find(s => (s.key === sectionKey || s.type === sectionKey)) || nextSec)
          : nextSec;
        setSectionLocal(sec);
        return sec;
      }
      throw err;
    }
  };

  const updatePageTitle = async (title) => {
    const payload = { slug, title };
    const updated = await AdminService.upsertPage(payload, token);
    return updated;
  };

  const value = useMemo(() => ({
    slug,
    token,
    editing: !!editing,
    sections,
    getValue: (sectionKey, fieldPath, fallback = '') => {
      const sec = findSection(sectionKey);
      const v = readNested(sec?.data || {}, fieldPath);
      return v !== undefined && v !== null && v !== '' ? v : fallback;
    },
    updateField,
    updatePageTitle
  }), [slug, token, editing, sections]);

  return (
    <InlineEditContext.Provider value={value}>
      {children}
    </InlineEditContext.Provider>
  );
};

export default InlineEditContext;